import { takeEvery, select, getContext } from 'redux-saga/effects';

import {
  INIT_DATADRIVEN_FILTERS,
  FETCH_DATADRIVEN_FILTERS_PARTITIONS,
  FETCH_DATADRIVEN_FILTERS_PARTITIONS_SUCCESS,
  FETCH_DATADRIVEN_FILTERS_PARTITIONS_ERROR,
  FETCH_DATADRIVEN_FILTERS_DIMENSIONS,
  FETCH_DATADRIVEN_FILTERS_DIMENSIONS_SUCCESS,
  FETCH_DATADRIVEN_FILTERS_DIMENSIONS_ERROR,
  FETCH_DATADRIVEN_FILTERS_OPTIONS,
  FETCH_DATADRIVEN_FILTERS_OPTIONS_SUCCESS,
  FETCH_DATADRIVEN_FILTERS_OPTIONS_ERROR,
  ADD_ENTERPRISE_FILTER,
  REMOVE_ENTERPRISE_FILTER,
  UPDATE_ENTERPRISE_FILTER,
  UPATE_PARTITION,
  ADD_PARTITION,
  REMOVE_PARTITION,
  type InitDataDrivenFiltersAppAction,
  type AddDataDrivenFilterAction,
  type RemoveDataDrivenFilterAction,
  type UpdateDataDrivenFilterAction,
  type AddPartitionAction,
  type RemovePartitionAction,
  type FetchPartitionsAction,
  type FetchPartitionsSuccessAction,
  type FetchPartitionsErrorAction,
  type FetchDimensionsAction,
  type FetchDimensionsSuccessAction,
  type FetchDimensionsErrorAction,
  type FetchOptionsAction,
  UpdatePartitionAction,
  UPDATE_ENTERPRISE_FILTER_OPTION,
} from 'store/actions';
import selectors from 'store/selectors';
import {
  ApiType,
  DataDrivenFiltersOptionType,
  DimensionType,
  FilterActions,
  FilterSlice,
  GetFilterBarDataType,
  PartitionType,
  SelectedPartitionType,
} from 'types';
import { generateId, getSetStateMethods } from 'utils';

const { setDataState, setUiState } =
  getSetStateMethods<FilterSlice>('dataDrivenFilters');

/**
 * Sagas for initializing the enterprise filters. Fetches the partitions.
 * The workspace id should be available in the state.
 * @param {InitDataDrivenFiltersAppAction} param0
 */
function* initDataDrivenFiltersSaga({
  onInit,
  onPartitionError,
  onPartitionSuccess,
}: InitDataDrivenFiltersAppAction): Generator<any> {
  try {
    const WorkspaceId = (yield select(selectors.getWorkspaceId)) as string;
    if (!WorkspaceId) throw new Error('WorkspaceId is required');
    const { getDataDrivenFiltersPartitions } = (yield select(
      (state) => state
    )) as FilterActions;

    yield getDataDrivenFiltersPartitions({
      WorkspaceId,
      onPartitionSuccess,
      onPartitionError,
    });
  } catch (error) {
    console.error(error);
  } finally {
    onInit?.();
  }
}

/**
 * Sagas for fetching the partitions. Passes the success and error callbacks.
 * @param {FetchPartitionsAction} param0
 */

function* fetchDataDrivenFiltersPartitionsSaga({
  WorkspaceId,
  onPartitionError,
  onPartitionSuccess,
}: FetchPartitionsAction): Generator<any> {
  const state = yield select((currentState) => currentState);
  const {
    getDataDrivenFiltersPartitionsError,
    getDataDrivenFiltersPartitionsSuccess,
  } = state as FilterActions;
  try {
    const api = (yield getContext('api')) as ApiType;
    yield setUiState({ loading: true });
    const getFilterBarData = (yield getContext(
      'getFilterBarData'
    )) as GetFilterBarDataType;
    const FilterBarValues = yield getFilterBarData?.(state);
    const data = (yield api.getDataDrivenFiltersPartitions({
      WorkspaceId,
      FilterBarValues,
    })) as PartitionType[];

    yield getDataDrivenFiltersPartitionsSuccess({
      callback: onPartitionSuccess,
      data,
    });
  } catch (error) {
    console.error(error);
    yield getDataDrivenFiltersPartitionsError({
      error,
      callback: onPartitionError,
    });
  } finally {
    yield setUiState({ loading: false });
  }
}

/**
 * Saga for setting the partitions in the state. This is called after the partitions are fetched successfully.
 * @param {FetchPartitionsSuccessAction} param0
 */

function* fetchDataDrivenFiltersPartitionsSuccessSaga({
  data,
  callback,
}: FetchPartitionsSuccessAction): Generator<any> {
  try {
    const partitions = data.map((p, index) => ({ ...p, order: index }));
    yield setDataState({ partitions, originalPartitions: partitions });
    yield callback?.();
  } catch (error) {
    console.error(error);
  }
}

/**
 * Sagas for setting the error in the state. This is called when there is an error fetching the partitions.
 * @param {FetchPartitionsErrorAction} param0
 */

function* fetchDataDrivenFiltersPartitionsErrorSaga({
  error,
}: FetchPartitionsErrorAction): Generator<any> {
  yield setUiState({ error });
}

/**
 * Sagas for adding a new partition to the state.
 * @param {AddPartitionAction} param0
 */
function* addPartitionDataDrivenFiltersaga({
  data,
  selectedPartitionIndex,
}: AddPartitionAction): Generator<any> {
  try {
    const { partition, id } = data || {};
    const selectedPartitions = (yield select(
      selectors.dataDrivenFilters.getSelectedPartitions
    )) as SelectedPartitionType[];
    const newSelectedPartitions = [
      ...(selectedPartitions || []),
      {
        data: {
          id: id || generateId(),
          order: selectedPartitionIndex,
          partition,
        },
      },
    ] as SelectedPartitionType[];
    yield setDataState({
      selectedPartitions: newSelectedPartitions,
    });
  } catch (error) {
    console.error(error);
  }
}
/**
 * Sagas for updating a partition to the state. when a new empty partition is added, it is removed from the list of partitions. the user can only select a partition once.
 * @param {UpdatePartitionAction} param0
 */
function* updatePartitionDataDrivenFiltersaga({
  selectedPartitionIndex,
  data,
  onDimensionSuccess,
  onDimensionError,
}: UpdatePartitionAction): Generator<any> {
  const { getDataDrivenFiltersDimensions } = (yield select(
    (state) => state
  )) as FilterActions;
  try {
    const { partition, id } = data || {};
    const selectedPartitions = (yield select(
      selectors.dataDrivenFilters.getSelectedPartitions
    )) as SelectedPartitionType[];
    const newSelectedPartitions = [...selectedPartitions];
    newSelectedPartitions[selectedPartitionIndex] = {
      data: {
        id: id || generateId(),
        ...(data || {}),
      },
    };
    const partitions = (yield select(
      selectors.dataDrivenFilters.getPartitions
    )) as PartitionType[];
    const newPartitions = partitions.filter(
      (p) => p.PublicKey !== partition.PublicKey
    );
    yield setDataState({
      selectedPartitions: newSelectedPartitions,
      partitions: newPartitions,
    });

    // Fetch dimensions for the selected partition. Pass callback
    yield getDataDrivenFiltersDimensions({
      selectedPartition: newSelectedPartitions[selectedPartitionIndex],
      selectedPartitionIndex,
      onDimensionSuccess,
      onDimensionError,
    });
  } catch (error) {
    console.error(error);
  }
}

/**
 * Sagas for removing a partition from the state. when a partition is removed, it is added back to the list of partitions.
 * @param {RemovePartitionAction} param0
 */
function* removePartitionDataDrivenFiltersaga({
  selectedPartitionIndex,
}: RemovePartitionAction): Generator<any> {
  try {
    const selectedPartitions = (yield select(
      selectors.dataDrivenFilters.getSelectedPartitions
    )) as SelectedPartitionType[];

    const partition = selectedPartitions.splice(selectedPartitionIndex, 1)?.[0];
    if (partition?.data?.partition) {
      const partitions = (yield select(
        selectors.dataDrivenFilters.getPartitions
      )) as PartitionType[];

      const newPartitions = [...partitions, partition?.data?.partition].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      yield setDataState({
        partitions: newPartitions,
      });
    }
    yield setDataState({
      selectedPartitions,
    });
  } catch (error) {
    console.error(error);
  }
}

/**
 * Sagas for adding a new filter to the state. A filter is added to the selected partition.
 * @param {AddDataDrivenFilterAction} param0
 */

function* addDataDrivenFiltersaga({
  filter,
  selectedPartitionIndex,
  callback,
}: AddDataDrivenFilterAction): Generator<any> {
  try {
    const selectedPartitions = (yield select(
      selectors.dataDrivenFilters.getSelectedPartitions
    )) as SelectedPartitionType[];

    selectedPartitions[selectedPartitionIndex] = {
      ...selectedPartitions[selectedPartitionIndex],
      data: {
        ...(selectedPartitions[selectedPartitionIndex]?.data || {}),
        selectedFilters: [
          ...(selectedPartitions?.[selectedPartitionIndex]?.data
            ?.selectedFilters || []),
          {
            ...(filter || {}),
            data: {
              id: filter?.data?.id || generateId(),
              ...(filter?.data || {}),
            },
          },
        ],
      },
    };

    yield setDataState({
      selectedPartitions,
    });
  } catch (error) {
    console.error(error);
  } finally {
    callback?.();
  }
}
/**
 * Sagas for fetching the dimensions for the selected partition. Passes the success and error callbacks.
 * @param {FetchDimensionsAction} param0
 */

function* fetchDataDrivenFilterDimesionsSaga({
  selectedPartitionIndex,
  selectedPartition,
  onDimensionError,
  onDimensionSuccess,
}: FetchDimensionsAction): Generator<any> {
  const selectedPartitions = (yield select(
    selectors.dataDrivenFilters.getSelectedPartitions
  )) as SelectedPartitionType[];
  const newSelectedPartitions = [...selectedPartitions];
  const state = yield select((currentState) => currentState);
  const {
    getDataDrivenFiltersDimensionsError,
    getDataDrivenFiltersDimensionsSuccess,
  } = state as FilterActions;
  try {
    // TODO: handle lopading state on current partition
    const api = (yield getContext('api')) as ApiType;

    const getFilterBarData = (yield getContext(
      'getFilterBarData'
    )) as GetFilterBarDataType;

    newSelectedPartitions[selectedPartitionIndex] = {
      ...selectedPartition,
      data: {
        ...(selectedPartition.data || {}),
        ...(newSelectedPartitions[selectedPartitionIndex]?.data || {}),
      },
      ui: {
        loading: true,
        error: null,
      },
    };

    yield setDataState({
      selectedPartitions: newSelectedPartitions,
    });

    const FilterBarValues = yield getFilterBarData?.(state);
    const dimensions = (yield api.getDataDrivenFiltersDimensions({
      partitionId: selectedPartition?.data?.partition?.PublicKey,
      FilterBarValues,
    })) as DimensionType[];
    if (dimensions) {
      newSelectedPartitions[selectedPartitionIndex] = {
        ...selectedPartition,
        data: {
          ...(selectedPartition.data || {}),
          ...(newSelectedPartitions[selectedPartitionIndex]?.data || {}),
          dimensions,
        },
        ui: {
          loading: false,
          error: null,
        },
      };

      yield setDataState({
        selectedPartitions: newSelectedPartitions,
      });
      yield getDataDrivenFiltersDimensionsSuccess({
        callback: onDimensionSuccess,
      });
    }
  } catch (error) {
    console.error(error);
    yield getDataDrivenFiltersDimensionsError({
      error,
      callback: onDimensionError,
    });
  } finally {
    newSelectedPartitions[selectedPartitionIndex] = {
      ...selectedPartition,
      data: {
        ...(selectedPartition.data || {}),
        ...(newSelectedPartitions[selectedPartitionIndex]?.data || {}),
      },
      ui: {
        loading: false,
        error: null,
      },
    };
  }
}

/**
 * Sagas for setting the dimensions in the state. This is called after the dimensions are fetched successfully.
 * @param {FetchDimensionsSuccessAction} param0
 */

function* fetchDataDrivenFilterDimesionsSuccessSaga({
  callback,
}: FetchDimensionsSuccessAction): Generator<any> {
  yield callback?.();
}

/**
 * Sagas for setting the error in the state. This is called when there is an error fetching the dimensions.
 * @param {FetchDimensionsErrorAction} param0
 */
function* fetchDataDrivenFilterDimesionsErrorSaga({
  error,
  callback,
}: FetchDimensionsErrorAction): Generator<any> {
  yield setUiState({ error });
  callback?.();
}

/**
 * Sagas for removing a filter from the state. A filter is removed from the selected partition.
 * @param {RemoveDataDrivenFilterAction} param0
 */
function* removeDataDrivenFiltersagas({
  selectedPartitionIndex,
  selectedFilterIndex,
  callback,
}: RemoveDataDrivenFilterAction): Generator<any> {
  try {
    const selectedPartitions = (yield select(
      selectors.dataDrivenFilters.getSelectedPartitions
    )) as SelectedPartitionType[];
    selectedPartitions[selectedPartitionIndex].data.selectedFilters?.splice(
      selectedFilterIndex,
      1
    );
    yield setDataState({
      selectedPartitions,
    });
  } catch (error) {
    console.error(error);
  } finally {
    callback?.();
  }
}

/**
 * Sagas for updating a filter in the state. A filter is updated in the selected partition. When a filter is updated, the options are reset and fetched for the filter.
 * @param {UpdateDataDrivenFilterAction} param0
 */
function* updateDataDrivenFiltersagas({
  selectedPartitionIndex,
  selectedFilterIndex,
  filter,
  searchText,
  callback,
  onOptionsSuccess,
  onOptionsError,
}: UpdateDataDrivenFilterAction): Generator<any> {
  try {
    const selectedPartitions = (yield select(
      selectors.dataDrivenFilters.getSelectedPartitions
    )) as SelectedPartitionType[];

    selectedPartitions[selectedPartitionIndex].data.selectedFilters[
      selectedFilterIndex
    ] = {
      ...(selectedPartitions[selectedPartitionIndex]?.data?.selectedFilters[
        selectedFilterIndex
      ] || {}),
      ...filter,
      data: {
        ...(selectedPartitions[selectedPartitionIndex]?.data?.selectedFilters[
          selectedFilterIndex
        ]?.data || {}),
        ...(filter?.data || {}),
        options: null,
        values: [],
      },
    };

    yield setDataState({
      selectedPartitions,
    });

    if (filter.data.dimension) {
      const { getDataDrivenFiltersOptions } = (yield select(
        (state) => state
      )) as FilterActions;
      yield getDataDrivenFiltersOptions({
        selectedPartitionIndex,
        selectedFilterIndex,
        searchText,
        onOptionsSuccess,
        onOptionsError,
      });
    }
  } catch (error) {
    console.error(error);
  } finally {
    callback?.();
  }
}

/**
 * Sagas for fetching the options for the selected filter. Passes the success and error callbacks.
 * @param {FetchOptionsAction} param0
 */
function* fetchDataDrivenFiltersOptionsSaga({
  selectedFilterIndex,
  selectedPartitionIndex,
  searchText,
  onOptionsError,
  onOptionsSuccess,
  callback,
}: FetchOptionsAction): Generator<any> {
  if (
    selectedFilterIndex === undefined ||
    selectedPartitionIndex === undefined
  ) {
    throw new Error(
      'selectedFilterIndex and selectedPartitionIndex are required'
    );
  }
  const state = yield select((currentState) => currentState);
  const {
    getDataDrivenFiltersOptionsError,
    getDataDrivenFiltersOptionsSuccess,
  } = state as FilterActions;
  const selectedPartitions = (yield select(
    selectors.dataDrivenFilters.getSelectedPartitions
  )) as SelectedPartitionType[];
  const newSelectedPartitions = [...selectedPartitions];
  const partition = newSelectedPartitions[selectedPartitionIndex];
  const filter = partition?.data?.selectedFilters?.[selectedFilterIndex];

  try {
    newSelectedPartitions[selectedPartitionIndex].data.selectedFilters[
      selectedFilterIndex
    ] = {
      ...filter,
      data: {
        ...filter?.data,
        options: null,
        values: [],
      },
      ui: {
        loading: true,
        error: null,
      },
    };
    yield setDataState({
      selectedPartitions: newSelectedPartitions,
    });
    const api = (yield getContext('api')) as ApiType;
    const getFilterBarData = (yield getContext(
      'getFilterBarData'
    )) as GetFilterBarDataType;
    const FilterBarValues = yield getFilterBarData?.(state);
    const options = (yield api.getDataDrivenFiltersOptions({
      partitionId: partition?.data?.partition?.PublicKey,
      dimensionId: filter?.data?.dimension?.FieldGuid,
      FilterBarValues,
      searchText,
    })) as DataDrivenFiltersOptionType;

    if (options) {
      newSelectedPartitions[selectedPartitionIndex].data.selectedFilters[
        selectedFilterIndex
      ] = {
        ...filter,
        data: {
          ...filter?.data,
          options,
        },
        ui: {
          loading: false,
          error: null,
        },
      };
    }

    newSelectedPartitions[selectedPartitionIndex].data.selectedFilters[
      selectedFilterIndex
    ] = {
      ...(newSelectedPartitions[selectedPartitionIndex].data.selectedFilters[
        selectedFilterIndex
      ] || {}),
      ...filter,
      data: {
        ...filter?.data,
        ...(newSelectedPartitions[selectedPartitionIndex].data.selectedFilters[
          selectedFilterIndex
        ]?.data || {}),
      },
      ui: {
        loading: false,
        error: null,
      },
    };

    yield setDataState({
      selectedPartitions: newSelectedPartitions,
    });
    yield getDataDrivenFiltersOptionsSuccess({ callback: onOptionsSuccess });
  } catch (error) {
    console.error(error);
    newSelectedPartitions[selectedPartitionIndex].data.selectedFilters[
      selectedFilterIndex
    ] = {
      ...(newSelectedPartitions[selectedPartitionIndex].data.selectedFilters[
        selectedFilterIndex
      ] || {}),
      ...filter,
      data: {
        ...filter?.data,
        ...(newSelectedPartitions[selectedPartitionIndex].data.selectedFilters[
          selectedFilterIndex
        ] || {}),
      },
      ui: {
        loading: false,
        error,
      },
    };
    yield getDataDrivenFiltersOptionsError({
      error,
      callback: onOptionsError,
    });
  } finally {
    yield callback?.();
  }
}

/**
 * Sagas for setting the options in the state. This is called after the options are fetched successfully.
 * @param {FetchDimensionsSuccessAction} param0
 */
function* fetchDataDrivenFiltersOptionsSuccessSaga({
  callback,
}: FetchDimensionsSuccessAction): Generator<any> {
  try {
    yield callback?.();
  } catch (error) {
    console.error(error);
  }
}

/**
 * Sagas called when there is an error fetching the options.
 * @param {FetchDimensionsErrorAction} param0
 */
function* fetchDataDrivenFiltersOptionsErrorSaga({
  error,
  callback,
}: FetchDimensionsErrorAction): Generator<any> {
  yield callback?.(error);
}

/**
 * Sagas for updating a filter in the state. A filter is updated in the selected partition. When a filter is updated, the options are reset and fetched for the filter.
 * @param {UpdateDataDrivenFilterAction} param0
 */
function* updateDataDrivenFiltersOptionSagas({
  selectedPartitionIndex,
  selectedFilterIndex,
  filter,
  searchText,
  callback,
}: UpdateDataDrivenFilterAction): Generator<any> {
  try {
    const selectedPartitions = (yield select(
      selectors.dataDrivenFilters.getSelectedPartitions
    )) as SelectedPartitionType[];

    selectedPartitions[selectedPartitionIndex].data.selectedFilters[
      selectedFilterIndex
    ] = {
      ...(selectedPartitions[selectedPartitionIndex]?.data?.selectedFilters[
        selectedFilterIndex
      ] || {}),
      ...filter,
      data: {
        ...(selectedPartitions[selectedPartitionIndex]?.data?.selectedFilters[
          selectedFilterIndex
        ]?.data || {}),
        ...(filter?.data || {}),
        options: {
          ...(selectedPartitions[selectedPartitionIndex]?.data?.selectedFilters[
            selectedFilterIndex
          ]?.data?.options || {}),
          ...(filter?.data || {})?.options,
          SearchText: searchText,
        },
      },
    };

    yield setDataState({
      selectedPartitions,
    });
  } catch (error) {
    console.error(error);
  } finally {
    callback?.();
  }
}

export default function* saga() {
  yield takeEvery(INIT_DATADRIVEN_FILTERS, initDataDrivenFiltersSaga);
  yield takeEvery(ADD_PARTITION, addPartitionDataDrivenFiltersaga);
  yield takeEvery(UPATE_PARTITION, updatePartitionDataDrivenFiltersaga);
  yield takeEvery(REMOVE_PARTITION, removePartitionDataDrivenFiltersaga);
  yield takeEvery(ADD_ENTERPRISE_FILTER, addDataDrivenFiltersaga);
  yield takeEvery(REMOVE_ENTERPRISE_FILTER, removeDataDrivenFiltersagas);
  yield takeEvery(UPDATE_ENTERPRISE_FILTER, updateDataDrivenFiltersagas);
  yield takeEvery(
    UPDATE_ENTERPRISE_FILTER_OPTION,
    updateDataDrivenFiltersOptionSagas
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_DIMENSIONS,
    fetchDataDrivenFilterDimesionsSaga
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_DIMENSIONS_ERROR,
    fetchDataDrivenFilterDimesionsErrorSaga
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_DIMENSIONS_SUCCESS,
    fetchDataDrivenFilterDimesionsSuccessSaga
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_PARTITIONS,
    fetchDataDrivenFiltersPartitionsSaga
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_PARTITIONS_SUCCESS,
    fetchDataDrivenFiltersPartitionsSuccessSaga
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_PARTITIONS_ERROR,
    fetchDataDrivenFiltersPartitionsErrorSaga
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_OPTIONS,
    fetchDataDrivenFiltersOptionsSaga
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_OPTIONS_SUCCESS,
    fetchDataDrivenFiltersOptionsSuccessSaga
  );
  yield takeEvery(
    FETCH_DATADRIVEN_FILTERS_OPTIONS_ERROR,
    fetchDataDrivenFiltersOptionsErrorSaga
  );
}
